<template>
    <div class="wizard-step">
        <b-row>
            <b-col cols="12">
                <StepIndicator :current-step="currentStep" :config="config" class="mb-5" @show-terms="$emit('show-terms')" />
                <h4>Payment Method</h4>
                <p class="text-muted">Provide your ACH bank account information for monthly automatic payments.</p>
            </b-col>
        </b-row>

        <b-row class="mt-4">
            <b-col cols="12">
                <b-card>
                    <h5>ACH Debit Account Information</h5>

                    <!-- Already has a payment method on this plan -->
                    <div v-if="plan.payment_method_id || plan.ach_provided">
                        <b-alert show variant="success" class="mb-4">
                            <i class="fas fa-check-circle mr-2"></i>
                            <strong>Payment Information Already Provided</strong><br>
                            Your payment information has already been provided for this agreement. You may proceed to the next step.
                        </b-alert>
                    </div>

                    <div v-else>
                        <!-- Payment method selection when existing methods available -->
                        <div v-if="existingPaymentMethods.length > 0" class="mb-4">
                            <b-form-group label="Select a payment method:">
                                <b-form-select v-model="selectedSource" @change="onSourceChange">
                                    <b-form-select-option value="existing" v-for="pm in existingPaymentMethods" :key="pm.id" :value="pm.id">
                                        {{pm.nickname || 'Account'}} ({{pm.type === 'checking' ? 'Checking' : 'Savings'}} ****{{pm.last_four}})
                                    </b-form-select-option>
                                    <b-form-select-option value="new">Add a new bank account</b-form-select-option>
                                </b-form-select>
                            </b-form-group>

                            <!-- Existing method selected -->
                            <div v-if="selectedSource !== 'new' && !existingMethodSubmitted">
                                <b-alert show variant="info" class="mt-3">
                                    <p><i class="fas fa-university mr-2"></i>
                                    <strong>Automatic Monthly Payments</strong></p>
                                    <p>Your monthly payments will be automatically debited from your selected bank account on the 1st of each month.</p>
                                    <p class="mb-0"><strong>{{selectedExistingMethod.nickname || 'Account'}}</strong> -
                                    {{selectedExistingMethod.type === 'checking' ? 'Checking' : 'Savings'}} ****{{selectedExistingMethod.last_four}}</p>
                                </b-alert>
                                <b-button
                                    variant="success"
                                    :disabled="isSubmitting"
                                    @click="useExistingMethod">
                                    <i v-if="isSubmitting" class="fas fa-spinner fa-spin mr-2"></i>
                                    <i v-else class="fas fa-check mr-2"></i>
                                    {{isSubmitting ? 'Saving...' : 'Use This Payment Method'}}
                                </b-button>
                            </div>

                            <div v-if="existingMethodSubmitted" class="payment-method-success mt-3">
                                <div class="text-success mb-2">
                                    <i class="fas fa-check-circle mr-2"></i>
                                    <strong>Payment Method Saved</strong>
                                </div>
                                <small class="text-muted">Your existing payment method has been linked to this agreement.</small>
                            </div>
                        </div>

                        <!-- New ACH form (shown when no existing methods OR user selects "new") -->
                        <div v-if="showNewForm">
                            <b-alert show variant="info" class="mt-4">
                                <p><i class="fas fa-university mr-2"></i>
                                <strong>Automatic Monthly Payments</strong></p>
                                <p>Your monthly payments will be automatically debited from the bank account you provide below on the 1st of each month. This information can be found on a physical check (see image below), obtained from your bank, or often accessed from online banking tools or apps.</p>
                            </b-alert>
                            <b-alert show variant="warning" class="mb-5 mt-4">
                                <i class="far fa-exclamation-triangle mr-2"></i>
                                <strong>Important:</strong> Your account number cannot be found on a credit or debit card or deposit slip.  Do not enter credit card information. Please note that some banks use "sub-accounts" which are not supported.  Verify with your bank if you are unsure.
                            </b-alert>
                            <b-row>
                                <b-col cols="12" md="6">
                                    <b-form-group label="Bank Routing Number *">
                                    <b-form-input
                                        required
                                        placeholder="Routing Number"
                                        v-model="achData.routing"
                                        v-mask="'#########'"
                                        @input="validateForm"
                                        :disabled="skipPaymentMethod"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group label="Verify Bank Routing Number *">
                                    <b-form-input
                                        required
                                        placeholder="Routing Number"
                                        v-model="achData.verifyRouting"
                                        v-mask="'#########'"
                                        @input="validateForm"
                                        :disabled="skipPaymentMethod"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col cols="12" md="6">
                                <b-form-group label="Account Number *">
                                    <b-form-input
                                        required
                                        placeholder="Account Number"
                                        v-model="achData.account"
                                        v-mask="'#################'"
                                        @input="validateForm"
                                        :disabled="skipPaymentMethod"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group label="Verify Account Number *">
                                    <b-form-input
                                        required
                                        placeholder="Account Number"
                                        v-model="achData.verifyAccount"
                                        v-mask="'#################'"
                                        @input="validateForm"
                                        :disabled="skipPaymentMethod"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col cols="12" md="4">
                                <b-form-group label="Account Type *">
                                    <b-form-select
                                        :options="accountTypes"
                                        required
                                        v-model="achData.type"
                                        @change="validateForm"
                                        :disabled="skipPaymentMethod"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="8">
                                <b-form-group label="Payment Method Nickname *">
                                    <b-form-input
                                        length="200"
                                        required
                                        v-model="achData.nickname"
                                        placeholder="e.g., My Checking Account"
                                        @input="validateForm"
                                        :disabled="skipPaymentMethod"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <div v-if="validationError" class="text-danger mb-3">
                            <i class="fas fa-exclamation-triangle mr-2"></i>{{validationError}}
                        </div>

                        <div v-if="submitError" class="text-danger mb-3">
                            <i class="fas fa-exclamation-triangle mr-2"></i>{{submitError}}
                        </div>

                        <div v-if="!paymentMethodSubmitted">
                            <b-button
                                variant="success"
                                :disabled="!canSubmit || isSubmitting || skipPaymentMethod"
                                @click="submitPaymentMethod">
                                <i v-if="isSubmitting" class="fas fa-spinner fa-spin mr-2"></i>
                                <i v-else class="fas fa-check mr-2"></i>
                                {{isSubmitting ? 'Submitting...' : 'Submit Payment Method'}}
                            </b-button>

                            <div class="skip-payment-option mt-4">
                                <b-form-checkbox v-model="skipPaymentMethod" @change="handleSkipChange">
                                    Skip for now
                                </b-form-checkbox>
                                <b-alert v-if="skipPaymentMethod" show variant="warning" class="mt-3">
                                    <i class="fas fa-exclamation-circle mr-2"></i>
                                    <strong>Important:</strong> You must add a payment method before your first payment is due.  This can be done at any time once your account is established.  It is your responsibility to keep your payment method up to date.
                                </b-alert>
                            </div>
                        </div>

                        <div v-else class="payment-method-success">
                            <div class="text-success mb-2">
                                <i class="fas fa-check-circle mr-2"></i>
                                <strong>Payment Method Saved</strong>
                            </div>
                            <small class="text-muted">Your bank account information has been securely saved for automatic monthly payments.</small>
                        </div>

                            <div class="border-top mt-4 pt-4">
                                <img src="@/assets/echeck.gif" class="echeck-image">
                            </div>
                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import {apiUrl} from '@/constants/config';
import axios from 'axios';
import StepIndicator from '@/components/sign-agreement/StepIndicator.vue';

export default {
    name: 'PaymentMethod',
    components: {
        StepIndicator
    },
    props: {
        plan: {
            type: Object,
            required: true
        },
        selectedPaymentMethod: {
            type: String,
            required: false
        },
        paymentMethodAcknowledged: {
            type: Boolean,
            required: true
        },
        currentStep: {
            type: Number,
            required: true
        },
        apiPath: {
            type: String,
            required: true
        },
        config: {
            type: Object,
            default: () => ({})
        },
        existingPaymentMethods: {
            type: Array,
            default: () => ([])
        }
    },
    data() {
        return {
            // Source selection: 'new' or a payment method ID
            selectedSource: this.existingPaymentMethods && this.existingPaymentMethods.length > 0
                ? this.existingPaymentMethods[0].id
                : 'new',
            existingMethodSubmitted: false,
            achData: {
                routing: '',
                verifyRouting: '',
                account: '',
                verifyAccount: '',
                type: null,
                nickname: ''
            },
            accountTypes: [
                {text: 'Select Account Type...', value: null, disabled: true},
                {text: 'Checking', value: 'checking'},
                {text: 'Savings', value: 'savings'}
            ],
            internalAcknowledged: this.paymentMethodAcknowledged,
            validationError: null,
            submitError: null,
            isSubmitting: false,
            paymentMethodSubmitted: false,
            skipPaymentMethod: false
        };
    },
    computed: {
        showNewForm() {
            // Show new form if no existing methods, or user explicitly chose "new"
            return this.existingPaymentMethods.length === 0 || this.selectedSource === 'new';
        },
        selectedExistingMethod() {
            if (this.selectedSource === 'new') return {};
            return this.existingPaymentMethods.find(pm => pm.id === this.selectedSource) || {};
        },
        canSubmit() {
            return this.achData.routing &&
                   this.achData.verifyRouting &&
                   this.achData.account &&
                   this.achData.verifyAccount &&
                   this.achData.type &&
                   this.achData.nickname &&
                   this.achData.routing === this.achData.verifyRouting &&
                   this.achData.account === this.achData.verifyAccount &&
                   !this.validationError;
        },
        isValid() {
            if (this.plan.payment_method_id) return true;
            if (this.plan.ach_provided) return true;
            if (this.paymentMethodSubmitted) return true;
            if (this.existingMethodSubmitted) return true;
            if (this.skipPaymentMethod) return true;
            return false;
        }
    },
    methods: {
        onSourceChange(value) {
            // Reset states when switching
            this.existingMethodSubmitted = false;
            this.paymentMethodSubmitted = false;
            this.skipPaymentMethod = false;
            this.submitError = null;
        },

        async useExistingMethod() {
            this.isSubmitting = true;
            this.submitError = null;

            try {
                const response = await axios.post(`${apiUrl}${this.apiPath}/set-payment-method`, {
                    existing_payment_method_id: this.selectedSource
                });

                if (response.data.success) {
                    this.existingMethodSubmitted = true;

                    this.$emit('audit-event', {
                        eventType: 'payment_method_saved',
                        data: {
                            type: 'existing',
                            paymentMethodId: this.selectedSource,
                            nickname: this.selectedExistingMethod.nickname
                        }
                    });

                    this.$emit('payment-method-saved', {
                        paymentMethodId: this.selectedSource,
                        lastFour: this.selectedExistingMethod.last_four,
                        nickname: this.selectedExistingMethod.nickname
                    });
                }
            } catch (error) {
                console.error('Error using existing payment method:', error);
                this.submitError = error.response?.data?.message || 'Failed to set payment method. Please try again.';
            } finally {
                this.isSubmitting = false;
            }
        },

        validateForm() {
            this.validationError = null;

            if (this.achData.routing && this.achData.verifyRouting &&
                this.achData.routing !== this.achData.verifyRouting) {
                this.validationError = 'Routing numbers do not match.';
            }

            if (this.achData.account && this.achData.verifyAccount &&
                this.achData.account !== this.achData.verifyAccount) {
                this.validationError = 'Account numbers do not match.';
            }

            this.$emit('update:selectedPaymentMethod', 'ach');
            this.$emit('ach-data-changed', this.achData);
        },

        async submitPaymentMethod() {
            this.isSubmitting = true;
            this.submitError = null;

            try {
                const response = await axios.post(`${apiUrl}${this.apiPath}/set-payment-method`, {
                    routing: this.achData.routing,
                    account: this.achData.account,
                    type: this.achData.type,
                    nickname: this.achData.nickname
                });

                if (response.data.success) {
                    this.paymentMethodSubmitted = true;

                    this.$emit('audit-event', {
                        eventType: 'payment_method_saved',
                        data: { type: 'ach', accountType: this.achData.type }
                    });

                    this.$emit('payment-method-saved', {
                        paymentMethodId: response.data.paymentMethodId,
                        lastFour: response.data.lastFour,
                        nickname: response.data.nickname
                    });
                }

            } catch (error) {
                console.error('Payment method submission error:', error);
                this.submitError = error.response?.data?.message || 'Failed to save payment method. Please try again.';
            } finally {
                this.isSubmitting = false;
            }
        },

        handleSkipChange() {
            if (this.skipPaymentMethod) {
                this.validationError = null;
                this.submitError = null;

                this.$emit('audit-event', {
                    eventType: 'payment_method_skipped',
                    data: {}
                });
            }
        }
    },
    watch: {
        internalAcknowledged(newVal) {
            this.$emit('update:paymentMethodAcknowledged', newVal);
        },
        isValid: {
            handler(valid) {
                this.$emit('validation-changed', valid);
            },
            immediate: true
        },
        existingPaymentMethods: {
            handler(methods) {
                if (methods && methods.length > 0 && this.selectedSource === 'new') {
                    this.selectedSource = methods[0].id;
                }
            },
            immediate: true
        }
    },
    mounted() {
        this.$emit('audit-event', {
            eventType: 'step_3_entered',
            data: {
                stepName: 'payment_method',
                hasExistingMethod: !!(this.plan.payment_method_id || this.plan.ach_provided),
                existingMethodCount: this.existingPaymentMethods.length
            }
        });

        this.$emit('update:selectedPaymentMethod', 'ach');
    }
};
</script>

<style lang="scss" scoped>
    @import '~@/assets/scss/_variables.scss';

    .card {
        border: 1px solid #e9ecef;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);

        h5, h6 {
            color: #464577;
            margin-bottom: 15px;
        }
    }

    .echeck-image {
        max-width: 100%;
        height: auto;
    }

    .schedule-info {
        .table {
            margin-bottom: 0;

            td {
                border: none;
                padding: 8px 12px;

                &:first-child {
                    width: 200px;
                    font-weight: 500;
                }
            }
        }
    }

    .acknowledgment {
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        padding: 20px;

        .form-check {
            .form-check-label {
                font-size: 14px;
                line-height: 1.4;
                color: #495057;
                margin-left: 8px;
                cursor: pointer;
            }

            .form-check-input:checked ~ .form-check-label {
                color: #464577;
                font-weight: 500;
            }
        }
    }

    .payment-method-success {
        padding: 15px;
        background: #f8f9fa;
        border: 1px solid #d4edda;
        border-radius: 8px;
    }
</style>
